import React, { useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import { AppLayout } from '../components/layout/AppLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, StatCard, Skeleton } from '../components/common';
import { analyticsAPI } from '../services/api';
import { useAsync, useMediaQuery } from '../hooks';
import { pct } from '../utils/helpers';

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-light)', borderRadius: 8, padding: '8px 12px', fontSize: '0.72rem', fontFamily: 'var(--font-mono)' }}>
      <p style={{ color: 'var(--text-muted)', marginBottom: 4 }}>{label}</p>
      {payload.map((p, i) => <p key={i} style={{ color: p.color || 'var(--cyan)' }}>{p.name}: <strong>{p.value}</strong></p>)}
    </div>
  );
};

export const AnalyticsPage = () => {
  const fetchSummary = useCallback(() => analyticsAPI.summary().then((r) => r.data.data), []);
  const fetchHours   = useCallback(() => analyticsAPI.opensByHour().then((r) => r.data.data), []);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const { data: summary, loading: sLoading } = useAsync(fetchSummary);
  const { data: hours,   loading: hLoading } = useAsync(fetchHours);

  // 24-hour chart — fill gaps
  const hourlyData = Array.from({ length: 24 }, (_, h) => {
    const found = hours?.find((d) => d._id?.hour === h);
    return {
      hour: `${String(h).padStart(2, '0')}:00`,
      opens: found?.count || 0,
    };
  });

  // Peak hour
  const peakHour  = hourlyData.reduce((max, d) => (d.opens > max.opens ? d : max), { hour: '—', opens: 0 });
  const totalHourOpens = hourlyData.reduce((s, d) => s + d.opens, 0);

  // Radar data for engagement health
  const radarData = summary
    ? [
        { metric: 'Open Rate',   value: Math.min(100, (summary.totalOpens  / Math.max(1, summary.totalEmails)) * 100) },
        { metric: 'Click Rate',  value: Math.min(100, (summary.totalClicks / Math.max(1, summary.totalEmails)) * 100) },
        { metric: 'Volume',      value: Math.min(100, summary.totalEmails / 10) },
        { metric: 'Reach',       value: Math.min(100, summary.uniqueRecipients / Math.max(1, summary.totalEmails) * 100) },
        { metric: 'Engagement',  value: summary.totalOpens > 0 ? Math.min(100, (summary.totalClicks / summary.totalOpens) * 100) : 0 },
      ]
    : [];

  // Bar colors by intensity
  const maxOpens = Math.max(...hourlyData.map((d) => d.opens), 1);

  return (
    <AppLayout>
      <PageHeader title="Analytics" subtitle="Aggregate performance across all tracked emails (last 30 days)" />
      <div style={{ padding: isMobile ? '16px' : '24px 32px', display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(5, 1fr)', gap: 16 }}>
          {sLoading ? Array(5).fill(0).map((_, i) => <Skeleton key={i} height={90} />) : (
            <>
              <StatCard label="Emails Sent"        value={summary?.totalEmails ?? 0}        icon="✉" />
              <StatCard label="Total Opens"         value={summary?.totalOpens  ?? 0}        icon="👁"  accent="var(--cyan)" />
              <StatCard label="Total Clicks"        value={summary?.totalClicks ?? 0}        icon="🔗"  accent="var(--amber)" />
              <StatCard label="Open Rate"           value={summary ? pct(summary.totalOpens, summary.totalEmails) : '—'}   icon="📊" accent="var(--green)" />
              <StatCard label="Unique Recipients"   value={summary?.uniqueRecipients ?? 0}   icon="👤" />
            </>
          )}
        </div>

        {/* Charts Row */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '3fr 2fr', gap: 20 }}>
          {/* Opens by Hour Bar Chart */}
          <Card style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <p style={{ fontSize: '0.68rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>Opens by Hour of Day</p>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>When your recipients engage most</p>
              </div>
              {!hLoading && peakHour.opens > 0 && (
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: 2 }}>PEAK HOUR</p>
                  <p style={{ fontSize: '1.1rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--cyan)' }}>{peakHour.hour}</p>
                  <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{peakHour.opens} opens</p>
                </div>
              )}
            </div>
            {hLoading ? <Skeleton height={220} /> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={hourlyData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                  <CartesianGrid stroke="#1e1e2e" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fill: '#4a4a65', fontSize: 9, fontFamily: 'Space Mono' }} tickLine={false} axisLine={false} interval={3} />
                  <YAxis tick={{ fill: '#4a4a65', fontSize: 10, fontFamily: 'Space Mono' }} tickLine={false} axisLine={false} />
                  <ReTooltip content={<ChartTooltip />} />
                  <Bar dataKey="opens" name="Opens" radius={[3, 3, 0, 0]}>
                    {hourlyData.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={entry.opens === 0 ? '#1e1e2e' : `rgba(0, 225, 200, ${0.2 + (entry.opens / maxOpens) * 0.8})`}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Engagement Radar */}
          <Card style={{ padding: '20px 24px' }}>
            <p style={{ fontSize: '0.68rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 20 }}>Engagement Health</p>
            {sLoading ? <Skeleton height={220} /> : (
              <ResponsiveContainer width="100%" height={220}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#1e1e2e" />
                  <PolarAngleAxis dataKey="metric" tick={{ fill: '#8888aa', fontSize: 10, fontFamily: 'Space Mono' }} />
                  <PolarRadiusAxis tick={false} axisLine={false} domain={[0, 100]} />
                  <Radar name="Performance" dataKey="value" stroke="#00e1c8" fill="#00e1c8" fillOpacity={0.15} strokeWidth={2} />
                  <ReTooltip content={<ChartTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>

        {/* Insight Cards */}
        {!sLoading && summary && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16 }}>
            {[
              {
                title: 'Click-to-Open Rate',
                value: summary.totalOpens > 0 ? pct(summary.totalClicks, summary.totalOpens) : '—',
                desc: 'Of openers who also clicked a link',
                color: 'var(--amber)',
              },
              {
                title: 'Avg Opens per Email',
                value: summary.totalEmails > 0 ? (summary.totalOpens / summary.totalEmails).toFixed(1) : '—',
                desc: 'Average number of opens per sent email',
                color: 'var(--cyan)',
              },
              {
                title: 'Best Sending Hour',
                value: peakHour.opens > 0 ? peakHour.hour : '—',
                desc: `${peakHour.opens} opens recorded this hour over 30 days`,
                color: 'var(--green)',
              },
            ].map((insight) => (
              <Card key={insight.title} style={{ padding: '20px 24px' }}>
                <p style={{ fontSize: '0.68rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>{insight.title}</p>
                <p style={{ fontSize: '2rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: insight.color, lineHeight: 1, marginBottom: 8 }}>{insight.value}</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{insight.desc}</p>
              </Card>
            ))}
          </div>
        )}

        {/* Tips */}
        <Card style={{ padding: isMobile ? '16px' : '20px 24px', borderColor: 'var(--border-accent)' }}>
          <p style={{ fontSize: '0.68rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--cyan)', marginBottom: 14 }}>💡 Optimisation Tips</p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: 16 }}>
            {[
              { tip: 'Send during peak hours', detail: peakHour.opens > 0 ? `Your recipients engage most at ${peakHour.hour}` : 'Collect more data to see patterns' },
              { tip: 'Keep subject lines short', detail: 'Emails with subjects under 50 characters see 12% higher open rates' },
              { tip: 'Personalise your links', detail: 'Use recipient names in link text to improve click-through rates' },
            ].map((t) => (
              <div key={t.tip}>
                <p style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>{t.tip}</p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{t.detail}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </AppLayout>
  );
};
